import { Routes, Route, Navigate, Link } from 'react-router-dom';
import { useAuth } from './components/AuthContext';
import Protected from './components/Protected';
import AppLayout from './components/AppLayout';

// Public pages
import Login from './pages/Login';
import Register from './pages/Register';
import Landing from './pages/LandingModern';
import ForgotPassword from './pages/ForgotPassword';
import DoctorVerification from './pages/DoctorVerification';
import ContactPage from './pages/ContactPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import HowItWorksPage from './pages/HowItWorksPage';
import OurGoalPage from './pages/OurGoalPage';

// Patient pages
import PatientDashboard from './pages/PatientDashboard';
import ReportIssue from './pages/ReportIssue';
import PatientOnboarding from './pages/PatientOnboarding';

// Doctor pages
import DoctorDashboard from './pages/DoctorDashboard';
import PriorityQueuePage from './pages/PriorityQueuePage';
import PopulationView from './pages/PopulationView';
import CaregiverDashboard from './pages/CaregiverDashboard';
import CaregiverRegister from './pages/CaregiverRegister';

// Shared pages (patient + doctor)
import HealthLog from './pages/HealthLog';
import MyHealthPage from './pages/MyHealthPage';
import CareTeamPage from './pages/CareTeamPage';
import CareRequests from './pages/CareRequests';
import MessagesPage from './pages/MessagesPage';
import SettingsPage from './pages/SettingsPage';
import Profile from './pages/Profile';
import HelpPage from './pages/HelpPage';
import DataConsent from './pages/DataConsent';
import NotificationPreferences from './pages/NotificationPreferences';
import MyDataPrivacy from './pages/MyDataPrivacy';
import SchedulePage from './components/SchedulePage';
import SymptomDetail from './pages/SymptomDetail';
import CareEventDetail from './pages/CareEventDetail';
import PatientDetailPage from './pages/PatientDetailPage';
import ChatRoom from './components/ChatRoom';

// Admin pages
import AdminHome from './pages/admin/AdminHome';
import AdminManagement from './pages/admin/AdminManagement';
import AdminOnboarding from './pages/admin/AdminOnboarding';
import DoctorManagement from './pages/admin/DoctorManagement';
import UserManagement from './pages/admin/UserManagement';
import AdminOnCall from './pages/admin/AdminOnCall';
import AdminPopulationHealth from './pages/admin/AdminPopulationHealth';
import AdminSettings from './pages/admin/AdminSettings';
import ComplianceAudit from './pages/admin/ComplianceAudit';
import DataManagement from './pages/admin/DataManagement';
import SystemNotifications from './pages/admin/SystemNotifications';
import AccessControl from './pages/admin/AccessControl';
import IntegrationManagement from './pages/admin/IntegrationManagement';

import ErrorBoundary from './components/ErrorBoundary';
import ConnectionStatus from './components/ConnectionStatus';
import GoogleAnalytics from './components/GoogleAnalytics';
import { AiCacheProvider } from './contexts/AiCacheContext';
import './styles/design-system.css';

// ── Helper: wrap a page in AppLayout ──
const Page = ({ title, showBack, children, roles, requireApprovedDoctor }) => (
  <Protected roles={roles} requireApprovedDoctor={requireApprovedDoctor}>
    <AppLayout title={title} showBack={showBack}>
      {children}
    </AppLayout>
  </Protected>
);

const App = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f9f9fd]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <AiCacheProvider>
    <ErrorBoundary>
      <div className="min-h-screen bg-[#f9f9fd]">
        <GoogleAnalytics />
        <ConnectionStatus />
        <main>
          <Routes>
            {/* ── Public routes ────────────────────────────────── */}
            <Route path="/caregiver-register" element={<CaregiverRegister />} />

            <Route path="/login"          element={user ? <Navigate to="/" replace /> : <Login />} />
            <Route path="/register"       element={user ? <Navigate to="/" replace /> : <Register />} />
            <Route path="/welcome"        element={user ? <Navigate to="/" replace /> : <Landing />} />
            <Route path="/forgot-password" element={user ? <Navigate to="/" replace /> : <ForgotPassword />} />
            {/* Only DOCTOR role has a verification flow; redirect everyone else to their home */}
            <Route
              path="/doctor-verification"
              element={
                user && user.role !== 'DOCTOR'
                  ? <Navigate to="/" replace />
                  : <DoctorVerification />
              }
            />
            <Route path="/contact"       element={<ContactPage />} />
            <Route path="/privacy"       element={<PrivacyPage />} />
            <Route path="/terms"         element={<TermsPage />} />
            <Route path="/how-it-works"  element={<HowItWorksPage />} />
            <Route path="/our-goal"      element={<OurGoalPage />} />

            {/* ── Root redirect ───────────────────────────────── */}
            <Route
              path="/"
              element={
                user ? (
                  user.role === 'CAREGIVER' ? <Navigate to="/caregiver-dashboard" replace /> :
                  user.role === 'USER'  ? <Navigate to="/dashboard" replace /> :
                  user.role === 'DOCTOR' ? (
                    user.doctorVerificationStatus === 'APPROVED'
                      ? <Navigate to="/doctor-dashboard" replace />
                      : <Navigate to="/doctor-verification" replace />
                  ) :
                  user.role === 'ADMIN' ? (
                    user.mustChangePassword
                      ? <Navigate to="/admin/onboarding" replace />
                      : <Navigate to="/admin" replace />
                  ) :
                  <Navigate to="/login" replace />
                ) : <Landing />
              }
            />

            {/* ── Caregiver routes ────────────────────────────── */}
            <Route path="/caregiver-dashboard" element={
              <Protected roles={['CAREGIVER']}><CaregiverDashboard /></Protected>
            } />

            {/* ── Patient routes ──────────────────────────────── */}
            <Route path="/onboarding" element={<Page title="Set Up Profile" roles={['USER']}><PatientOnboarding /></Page>} />
            <Route path="/dashboard"  element={<Page title="Today"          roles={['USER']}><PatientDashboard /></Page>} />
            <Route path="/report"     element={<Page title="Report Issue"   roles={['USER']}><ReportIssue /></Page>} />

            {/* ── Doctor routes ───────────────────────────────── */}
            <Route path="/doctor-dashboard" element={<Page title="Clinician Hub"   roles={['DOCTOR']} requireApprovedDoctor><DoctorDashboard /></Page>} />
            <Route path="/priority-queue"   element={<Page title="Priority Queue"  roles={['DOCTOR']} requireApprovedDoctor><PriorityQueuePage /></Page>} />
            <Route path="/population"       element={<Page title="Patient Panel"   roles={['DOCTOR']} requireApprovedDoctor><PopulationView /></Page>} />
            <Route path="/schedule"         element={<Page title="Schedule"        roles={['DOCTOR']} requireApprovedDoctor><SchedulePage /></Page>} />

            {/* ── Shared routes (patient + doctor) ────────────── */}
            <Route path="/my-health"     element={<Page title="My Health"      roles={['USER']}><MyHealthPage /></Page>} />
            <Route path="/health-log"    element={<Page title="Patient Log"    roles={['DOCTOR']} requireApprovedDoctor><HealthLog /></Page>} />
            <Route path="/care-team"     element={<Page title="Care Team"      roles={['USER', 'DOCTOR']} requireApprovedDoctor><CareTeamPage /></Page>} />
            <Route path="/care-requests" element={<Page title="Care Requests"  roles={['USER', 'DOCTOR']} requireApprovedDoctor><CareRequests /></Page>} />
            <Route path="/messages"      element={<Page title="Messages"       roles={['USER', 'DOCTOR']} requireApprovedDoctor><MessagesPage /></Page>} />
            {/* /records was a doctor-only page — redirect to the equivalent for each role */}
            <Route path="/records" element={
              user?.role === 'DOCTOR' ? <Navigate to="/health-log" replace /> : <Navigate to="/my-health" replace />
            } />
            <Route path="/settings" element={<Page title="Settings" roles={['USER', 'DOCTOR', 'ADMIN']}><SettingsPage /></Page>} />

            {/* ── Detail pages (with back button) ─────────────── */}
            <Route path="/symptom/:id"    element={<Page title="Symptom Detail" showBack roles={['USER', 'DOCTOR']} requireApprovedDoctor><SymptomDetail /></Page>} />
            <Route path="/care-event/:id" element={<Page title="Care Event"     showBack roles={['USER', 'DOCTOR']} requireApprovedDoctor><CareEventDetail /></Page>} />
            <Route path="/patient/:id"    element={<Page title="Patient"        showBack roles={['DOCTOR', 'ADMIN']} requireApprovedDoctor><PatientDetailPage /></Page>} />
            <Route path="/chat/:symptomId" element={<Page title="Chat"          showBack roles={['USER', 'DOCTOR']} requireApprovedDoctor><ChatRoom /></Page>} />

            {/* ── Common pages ────────────────────────────────── */}
            <Route path="/profile"                  element={<Page title="Profile"           roles={['USER', 'DOCTOR', 'ADMIN']}><Profile /></Page>} />
            <Route path="/notification-preferences" element={<Page title="Notifications" showBack roles={['USER', 'DOCTOR', 'ADMIN']}><NotificationPreferences /></Page>} />
            <Route path="/my-data"                  element={<Page title="My Data & Privacy" showBack roles={['USER']}><MyDataPrivacy /></Page>} />
            <Route path="/data-consent"             element={<Page title="Doctor Data Access" showBack roles={['USER']}><DataConsent /></Page>} />
            <Route path="/help"                     element={<Page title="Help & Support"    roles={['USER', 'DOCTOR', 'ADMIN']}><HelpPage /></Page>} />

            {/* ── Admin routes ────────────────────────────────── */}
            {/* Full-screen onboarding — no AppLayout chrome, shown on first login */}
            <Route path="/admin/onboarding"        element={<Protected roles={['ADMIN']}><AdminOnboarding /></Protected>} />
            <Route path="/admin"                   element={<Page title="Platform Overview"  roles={['ADMIN']}><AdminHome /></Page>} />
            <Route path="/admin/admins"            element={<Page title="Admin Accounts"    showBack roles={['ADMIN']}><AdminManagement /></Page>} />
            <Route path="/admin/doctors"           element={<Page title="Doctor Management" roles={['ADMIN']}><DoctorManagement /></Page>} />
            <Route path="/admin/users"             element={<Page title="User Management"   roles={['ADMIN']}><UserManagement /></Page>} />
            <Route path="/admin/on-call"           element={<Page title="On-Call Schedule"  roles={['ADMIN']}><AdminOnCall /></Page>} />
            <Route path="/admin/population"        element={<Page title="Population Health" roles={['ADMIN']}><AdminPopulationHealth /></Page>} />
            <Route path="/admin/settings"          element={<Page title="System Settings"   roles={['ADMIN']}><AdminSettings /></Page>} />
            <Route path="/admin/compliance"        element={<Page title="Compliance & Audit"     showBack roles={['ADMIN']}><ComplianceAudit /></Page>} />
            <Route path="/admin/data-management"   element={<Page title="Data Management"        showBack roles={['ADMIN']}><DataManagement /></Page>} />
            <Route path="/admin/notifications"     element={<Page title="System Notifications"   showBack roles={['ADMIN']}><SystemNotifications /></Page>} />
            <Route path="/admin/access-control"    element={<Page title="Access Control"         showBack roles={['ADMIN']}><AccessControl /></Page>} />
            <Route path="/admin/integrations"      element={<Page title="Integrations"           roles={['ADMIN']}><IntegrationManagement /></Page>} />

            {/* ── 404 ─────────────────────────────────────────── */}
            <Route
              path="*"
              element={
                <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-teal-50">
                  <div className="text-center">
                    <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                      <span className="text-5xl font-bold text-white">404</span>
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Page not found</h1>
                    <p className="text-gray-600 mb-6">The page you're looking for doesn't exist.</p>
                    <Link to="/" className="btn btn-primary">Go Home</Link>
                  </div>
                </div>
              }
            />
          </Routes>
        </main>
      </div>
    </ErrorBoundary>
    </AiCacheProvider>
  );
};

export default App;
